// INSIDO: organization profile service (module 02-organizacoes).
// Phase 1 keeps the enterprise profile on this device only (localStorage);
// Phase 2 swaps the implementation for the additive `/org/profile/:server_id`
// backend endpoint without touching any consumer — keep this interface stable.

/**
 * Business details attached to an organization (a `Server` upstream).
 * Field names intentionally match the planned `org_profiles` collection.
 */
export interface OrganizationProfile {
  /** Registered company name */
  legal_name: string;
  /** Portuguese tax identification number (NIF), 9 digits */
  tax_id: string;
  /** Registered address */
  address: string;
  /** Email domain of the company, e.g. `example.com` */
  email_domain: string;
  /** Administrative contact (email or phone) */
  contact: string;
}

/**
 * Data access contract for organization profiles.
 *
 * Phase 2 implementation maps to:
 * - `getProfile`  → `GET /org/profile/:server_id`
 * - `saveProfile` → `PUT /org/profile/:server_id`
 */
export interface OrganizationProfileService {
  /**
   * Fetch the profile for an organization
   * @param serverId Upstream server id
   * @returns The stored profile, or `null` when none exists yet
   */
  getProfile(serverId: string): Promise<OrganizationProfile | null>;

  /**
   * Persist the profile for an organization
   * @param serverId Upstream server id
   * @param profile Profile to store
   */
  saveProfile(serverId: string, profile: OrganizationProfile): Promise<void>;
}

/** Whether the profile is stored on this device only (Phase 1 limitation) */
export const ORGANIZATION_PROFILE_IS_LOCAL = true;

const STORAGE_KEY_PREFIX = "insido:org-profile:";

/**
 * Phase 1 backing store: localStorage, keyed by server id.
 */
class LocalStorageOrganizationProfileService implements OrganizationProfileService {
  async getProfile(serverId: string): Promise<OrganizationProfile | null> {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + serverId);
    if (!raw) return null;

    try {
      const value = JSON.parse(raw) as Partial<OrganizationProfile>;
      return {
        legal_name: value.legal_name ?? "",
        tax_id: value.tax_id ?? "",
        address: value.address ?? "",
        email_domain: value.email_domain ?? "",
        contact: value.contact ?? "",
      };
    } catch {
      return null;
    }
  }

  async saveProfile(
    serverId: string,
    profile: OrganizationProfile,
  ): Promise<void> {
    localStorage.setItem(
      STORAGE_KEY_PREFIX + serverId,
      JSON.stringify(profile),
    );
  }
}

/** Singleton used by the client; swap here for the Phase 2 API-backed service */
export const organizationProfileService: OrganizationProfileService =
  new LocalStorageOrganizationProfileService();

/**
 * Validate a Portuguese tax id (NIF): exactly 9 digits
 * @param taxId Candidate value (empty is considered valid / not filled in)
 */
export function isValidTaxId(taxId: string): boolean {
  return taxId === "" || /^\d{9}$/.test(taxId);
}

/**
 * Validate an email domain, e.g. `example.com`
 * @param domain Candidate value (empty is considered valid / not filled in)
 */
export function isValidEmailDomain(domain: string): boolean {
  return (
    domain === "" ||
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(
      domain,
    )
  );
}
